import { AnalyticsStore } from 'bernie-plugin-mobx';
import { FlightsTravelerDetailsInput, FlightsTravelerType } from '__generated__/api/types';
import { formatDateToIsoString } from './date-formats-by-locale';
import { getFlightsSearchPageKey, isFlightInformation } from './page-identity';
import { checkIfServerSideRendering } from 'utility';
import { getAdBlockerEventParams } from 'bernie-plugin-ads-loader-script';

import { IntersectionTrackerProps } from '@shared-ui/clickstream-analytics-context';

interface MesoTargetingTravelerInfoType {
  adults: number;
  kids: number;
}

export interface ViewabilityAnalytics {
  referrerId: string;
  linkName: string;
}

export const getTravelersInfo = (
  travelerDetails: FlightsTravelerDetailsInput[] | FlightsTravelerDetailsInput
): MesoTargetingTravelerInfoType => {
  if (!travelerDetails) {
    return undefined;
  }
  const travelerDetailsArray = travelerDetails instanceof Array ? travelerDetails : [travelerDetails];
  const travelers = {
    [FlightsTravelerType.ADULT]: 0,
    [FlightsTravelerType.CHILD]: 0,
    [FlightsTravelerType.INFANT_IN_LAP]: 0,
    [FlightsTravelerType.INFANT_IN_SEAT]: 0
  };

  travelerDetailsArray.forEach(traveler => (travelers[traveler.travelerType] = traveler.count));

  const adults = travelers[FlightsTravelerType.ADULT];
  const kids =
    travelers[FlightsTravelerType.CHILD] +
      travelers[FlightsTravelerType.INFANT_IN_LAP] +
      travelers[FlightsTravelerType.INFANT_IN_SEAT] || undefined;

  return { adults, kids };
};

export const isNewTargeting = (currentTargeting, savedTargeting) => {
  if (savedTargeting === null) {
    return true;
  }
  for (const key in currentTargeting) {
    if (currentTargeting[key] !== savedTargeting[key]) {
      return true;
    }
  }

  return false;
};

export const buildTargeting = (flightsSelectedJourneyReview, searchForm, page, searchCriteria, advertising) => {
  let destinationLocation,
    originLocation,
    departureDate,
    returnDate = '',
    travelersInfo;

  if (isFlightInformation(page)) {
    const journeyInformation = flightsSelectedJourneyReview && flightsSelectedJourneyReview[0].journeyInformation;
    destinationLocation = journeyInformation?.destinationAirportCode;
    originLocation = journeyInformation?.originAirportCode;
  } else {
    const pageKey = getFlightsSearchPageKey(page.pageId);

    const { travelerDetails, journeyCriteria } = searchCriteria;
    travelersInfo = getTravelersInfo(travelerDetails);
    destinationLocation = searchForm?.journeys[pageKey]?.destinationLocation?.airportCode;
    originLocation = searchForm?.journeys[pageKey]?.originLocation?.airportCode;
    departureDate =
      journeyCriteria?.[pageKey]?.departureDate?.day && formatDateToIsoString(journeyCriteria[pageKey]?.departureDate);
    returnDate =
      journeyCriteria?.[pageKey + 1]?.departureDate?.day &&
      formatDateToIsoString(journeyCriteria[pageKey + 1]?.departureDate);
  }

  window.meso = window.meso ?? {};
  window.meso.targeting = window.meso.targeting ?? [];
  const targeting = {
    dest: destinationLocation,
    origin: originLocation,
    locResolver: 'tla',
    dateStart: departureDate,
    dateEnd: returnDate,
    pageName: advertising?.triggeredPageName,
    ...travelersInfo
  };

  return targeting;
};

export const fireTracking = (trackingUrl: string) => {
  if (trackingUrl) {
    window.meso = window.meso || {};
    window.meso.beacons = window.meso.beacons || [];
    window.meso.beacons.push({ type: 'beacon', url: trackingUrl });
  }
};

export const isAdBlockEnabled = (): boolean =>
  checkIfServerSideRendering() || !(window?.meso?.adblock?.enabled === false);

export const fireAdBlockerEvent = (analyticsStore: AnalyticsStore) => {
  const { referrer, linkName } = getAdBlockerEventParams();
  analyticsStore.trackEvent(referrer, linkName, null, null, true);
};

export const getParsedDate = (dateAsIsoString: string) => {
  if (!dateAsIsoString) {
    return undefined;
  }

  const splittedIsoDate = dateAsIsoString.split('-').map(dateEntry => parseInt(dateEntry));

  return { year: splittedIsoDate[0], month: splittedIsoDate[1], day: splittedIsoDate[2] };
};

export const getInterSectionTrackerProps = (viewabilityAnalytics: ViewabilityAnalytics): IntersectionTrackerProps => {
  const { linkName, referrerId } = viewabilityAnalytics || {};

  if (linkName && referrerId) {
    return {
      referrerId: referrerId,
      linkName: linkName,
      delay: false,
      trackOnce: true
    };
  }

  return null;
};
